import * as React from 'react'
import {
  filterNodesBySection,
  useCompareBranchesQuery,
  useGetIngestModificationsQuery,
  useGetMainBranchQuery,
  type TaggedNode,
  type TaggedRel
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Card, CardContent } from '../../../components/ui/card'
import { Skeleton } from '../../../components/ui/skeleton'
import { StructureTree } from './StructureTree'

interface CreatedEntitiesProps {
  /** The job; its write log is the source for "what it built". */
  jobId?: string
  /** The job's branch; nodes hydrate from it and the open button switches to it. */
  branchId?: string
  /** Scope to one section's entities; null = the job-level view. */
  sectionId?: string | null
}

interface ModificationsModel {
  added: readonly TaggedNode[]
  scopedRels: readonly TaggedRel[]
  nodesAdded: number
  relsAdded: number
  truncated: boolean
}

function primaryLabel(labels: readonly string[] | undefined): string {
  return labels && labels.length > 0 ? labels[0] : 'Node'
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="font-mono text-xl font-semibold tracking-tight text-foreground tabular-nums">
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

/**
 * "What it built" — the agent's contribution to the job branch. Primary source
 * is the durable write log (`getIngestModifications`): the union of the job's write
 * batches, hydrated by id from the branch — O(modifications), no whole-graph diff.
 * Jobs from before the log fall back to the branch compare (`source = job
 * branch` vs `target = main`). A build readout (node / relationship counts),
 * count-by-label chips, and a `StructureTree`; honest totals-only fallback when
 * a compare diff is too large to itemize.
 */
export function CreatedEntities({ jobId, branchId, sectionId = null }: CreatedEntitiesProps) {
  // No polling here — AgentIngestPage polls the write-log watermark and
  // refetches this shared RTK cache entry when COUNT/MAX(id) moves.
  const { data: modifications, isLoading: modificationsLoading } = useGetIngestModificationsQuery(
    jobId ?? '',
    {
      skip: !jobId,
      refetchOnMountOrArgChange: true
    }
  )
  const hasLog = modifications?.hasLog === true
  const noLog = modifications?.hasLog === false

  // Compare fallback: only for jobs with no write log. Gated on `noLog` so the
  // expensive branch-vs-main diff never fires while modifications is still
  // resolving, nor at all once the log is present.
  const { data: main } = useGetMainBranchQuery()
  const { data: compare, isLoading: compareLoading } = useCompareBranchesQuery(
    { targetId: main?.id ?? '', sourceId: branchId ?? '' },
    {
      skip: !noLog || !main?.id || !branchId,
      refetchOnMountOrArgChange: true
    }
  )

  const model = React.useMemo<ModificationsModel>(() => {
    if (hasLog) {
      const allAdded = modifications?.nodes ?? []
      const allRels = modifications?.relationships ?? []
      const sbn = modifications?.sectionsByNode ?? {}
      const added = sectionId ? allAdded.filter((n) => sbn[n.id]?.includes(sectionId)) : allAdded
      const ids = new Set(added.map((n) => n.id))
      const scopedRels = sectionId
        ? allRels.filter((r) => ids.has(r.startId) && ids.has(r.endId))
        : allRels
      return {
        added,
        scopedRels,
        nodesAdded: sectionId ? added.length : (modifications?.counts?.nodesAdded ?? added.length),
        relsAdded: sectionId
          ? scopedRels.length
          : (modifications?.counts?.relsAdded ?? scopedRels.length),
        truncated: false
      }
    }
    const allAdded = compare?.nodes?.added ?? []
    const addedRels = compare?.relationships?.added ?? []
    const truncated = compare?.truncated ?? false
    // Truncated compare carries totals only, which can't be filtered — stays
    // job-level and says so below.
    const added = truncated ? allAdded : filterNodesBySection(allAdded, sectionId)
    const scoped = sectionId !== null && !truncated
    const ids = new Set(added.map((n) => n.id))
    const scopedRels = scoped
      ? addedRels.filter((r) => ids.has(r.startId) && ids.has(r.endId))
      : addedRels
    return {
      added,
      scopedRels,
      nodesAdded: scoped ? added.length : (compare?.counts?.nodesAdded ?? 0),
      relsAdded: scoped ? scopedRels.length : (compare?.counts?.relsAdded ?? 0),
      truncated
    }
  }, [hasLog, modifications, compare, sectionId])

  const { added, scopedRels, nodesAdded, relsAdded, truncated } = model
  const scoped = sectionId !== null && !truncated
  const isLoading = modificationsLoading || (noLog && compareLoading && !compare)

  const byLabel = React.useMemo(() => {
    const m = new Map<string, number>()
    for (const n of added) {
      const label = primaryLabel(n.labels)
      m.set(label, (m.get(label) ?? 0) + 1)
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  }, [added])

  return (
    // Fixed height matching the Conversation card: the structure tree fills the
    // remaining space and scrolls internally, so expanding it never reflows the
    // page (the Scratchpad below stays put).
    <Card className="h-[36rem] overflow-hidden">
      <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
        {!branchId ? (
          <p className="m-0 text-sm text-muted-foreground">No branch assigned to this job yet.</p>
        ) : isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-6 w-44" />
            <Skeleton className="h-5 w-5/6" />
            <Skeleton className="h-5 w-2/3" />
          </div>
        ) : nodesAdded === 0 ? (
          <p className="m-0 text-sm text-muted-foreground">
            {scoped
              ? 'Nothing in this section yet — entities appear here as its parser writes them.'
              : 'Nothing on the branch yet — entities appear here as the agent writes them.'}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <Stat value={nodesAdded} label={nodesAdded === 1 ? 'node' : 'nodes'} />
              <Stat value={relsAdded} label={relsAdded === 1 ? 'relationship' : 'relationships'} />
            </div>

            {truncated ? (
              <p className="m-0 text-xs text-muted-foreground">
                Too many changes to itemize — showing job-level totals only.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {byLabel.map(([label, count]) => (
                    <Badge key={label} variant="secondary" className="gap-1 font-normal">
                      {label}
                      <span className="font-mono text-muted-foreground tabular-nums">{count}</span>
                    </Badge>
                  ))}
                </div>

                <StructureTree
                  nodes={added}
                  relationships={scopedRels}
                  branchId={branchId}
                  jobId={jobId}
                />
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default CreatedEntities
